import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Eye, Star, MapPin, ToggleLeft, ToggleRight, AlertCircle } from 'lucide-react';

export default function MyListingsPage() {
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState(null);

  const { data, isLoading } = useQuery('my-listings', () =>
    api.get('/listings/owner/my-listings').then(r => r.data)
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/listings/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('my-listings');
        setDeleteId(null);
      }
    }
  );

  const typeLabels = { venue: 'ملعب/منشأة', equipment: 'معدات', service: 'خدمة' };
  const typeColors = { venue: 'bg-green-500/20 text-green-400', equipment: 'bg-blue-500/20 text-blue-400', service: 'bg-purple-500/20 text-purple-400' };

  if (isLoading) return (
    <div className="space-y-4">
      {[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}
    </div>
  );

  const listings = data?.listings || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">إعلاناتي</h1>
          <p className="text-slate-400 mt-1">{listings.length} إعلان مسجل</p>
        </div>
        <Link to="/dashboard/listings/add" className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          إضافة إعلان
        </Link>
      </div>

      {listings.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center">
          <div className="w-20 h-20 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus size={36} className="text-primary-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">لا توجد إعلانات بعد</h3>
          <p className="text-slate-400 mb-6">أضف أول إعلان لك وابدأ في استقبال الحجوزات</p>
          <Link to="/dashboard/listings/add" className="btn-primary">إضافة إعلان جديد</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map(listing => (
            <div key={listing.id} className="glass rounded-2xl p-5 flex items-center gap-5 hover:border-primary-500/30 transition-colors">
              <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                {listing.primary_image ? (
                  <img src={`${process.env.REACT_APP_API_URL}/${listing.primary_image}`} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                    <Eye size={24} className="text-slate-500" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-white font-bold truncate">{listing.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[listing.listing_type]}`}>
                    {typeLabels[listing.listing_type]}
                  </span>
                  {listing.is_featured === 1 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">مميز</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span className="flex items-center gap-1"><MapPin size={13} />{listing.city}</span>
                  <span className="flex items-center gap-1"><Star size={13} className="text-amber-400" />{listing.avg_rating ? Number(listing.avg_rating).toFixed(1) : 'لا تقييم'}</span>
                  <span>{listing.views_count || 0} مشاهدة</span>
                  <span className="text-primary-400 font-medium">{listing.price_per_hour} {listing.currency}/ساعة</span>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${listing.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-600/50 text-slate-400'}`}>
                  {listing.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  {listing.is_active ? 'نشط' : 'متوقف'}
                </div>
                <Link to={`/listings/${listing.uuid}`} className="p-2 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white transition-colors" title="عرض">
                  <Eye size={16} />
                </Link>
                <Link to={`/dashboard/listings/edit/${listing.uuid}`} className="p-2 rounded-lg bg-slate-700/50 text-slate-400 hover:text-primary-400 transition-colors" title="تعديل">
                  <Edit size={16} />
                </Link>
                <button onClick={() => setDeleteId(listing.id)} className="p-2 rounded-lg bg-slate-700/50 text-slate-400 hover:text-red-400 transition-colors" title="حذف">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass rounded-2xl p-8 max-w-sm w-full text-center">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">تأكيد الحذف</h3>
            <p className="text-slate-400 mb-6">هل أنت متأكد من حذف هذا الإعلان؟ لا يمكن التراجع عن هذا الإجراء.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 btn-secondary">إلغاء</button>
              <button
                onClick={() => deleteMutation.mutate(deleteId)}
                disabled={deleteMutation.isLoading}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl font-medium transition-colors"
              >
                {deleteMutation.isLoading ? 'جارٍ الحذف...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
